import { memo, useCallback } from 'react';
import {
	LuMove,
	LuChevronDown,
	LuChevronRight,
	LuX,
	LuMapPin,
	LuRefreshCw,
	LuRuler,
	LuSatellite,
	LuMap,
	LuTriangleAlert,
} from 'react-icons/lu';

import {
	SidebarSection,
	SectionTitle,
	SectionContent,
	FormGroup,
	ToggleGroup,
	Toggle,
	AdvancedToggle,
	ActionButton,
	FlightStatsCard,
	StatsGrid,
	StatItem,
} from './FlightPlannerStyles';
import ProjectInfoSection from './ProjectInfoSection';
import AiModelSection from './AiModelSection';
import DspCameraSettings from './DspCameraSettings';
import DspMissionStats from './DspMissionStats';
import { DSP_LENSES, DSP_TEMPLATES } from '../constants/flightConstants';
import { FEATURE_FLAGS } from '@constants/featureFlags';

/**
 * Step 3 (DSP) — Digital Sample Plot configuration panel.
 * Template selection, camera/altitude settings, point placement, mission stats.
 */
const WorkflowDspConfig = ({
	projectSettings,
	onProjectSettingsChange,
	detectedMunicipality,
	dspSettings,
	onDspSettingsChange,
	waypoints,
	polygonPoints,
	isAddingDsp,
	onToggleAddingDsp,
	onClearWaypoints,
	onOptimizeRoute,
	availableAiModels,
	showDspSettings,
	onToggleDspSettings,
	showDspAdvanced,
	onToggleDspAdvanced,
	showDistanceConstraints,
	onToggleDistanceConstraints,
	calculateDSPAltitude,
	onGenerateAutoCoverage,
	onDSPTemplateChange,
}) => {
	const currentAltitude = calculateDSPAltitude(dspSettings.plotArea, dspSettings.lens);
	const currentDspTemplate = DSP_TEMPLATES[dspSettings.template];

	const updateDsp = useCallback(
		(field, value) => onDspSettingsChange(prev => ({ ...prev, [field]: value })),
		[onDspSettingsChange]
	);

	const updateProjectField = useCallback(
		(field, value) => onProjectSettingsChange(prev => ({ ...prev, [field]: value })),
		[onProjectSettingsChange]
	);

	return (
		<>
			<ProjectInfoSection
				projectSettings={projectSettings}
				onSettingsChange={onProjectSettingsChange}
				detectedMunicipality={detectedMunicipality}
				namePlaceholder="My DSP Project"
			/>

			<SidebarSection>
				<SectionTitle>DSP Template</SectionTitle>
				<SectionContent>
					<FormGroup>
						<label>Template:</label>
						<select
							value={dspSettings.template}
							onChange={e => onDSPTemplateChange(e.target.value)}>
							{Object.entries(DSP_TEMPLATES).map(([id, template]) => (
								<option key={id} value={id}>
									{template.name}
								</option>
							))}
						</select>
						<small style={{ color: '#888', fontSize: '0.8rem' }}>
							{currentDspTemplate?.description}
						</small>
					</FormGroup>

					<FlightStatsCard>
						<h4>
							<LuMove
								size={14}
								style={{ verticalAlign: 'middle', marginRight: '4px' }}
							/>{' '}
							Calculated Settings
						</h4>
						<StatsGrid>
							<StatItem>
								<div className="stat-label">Flight Altitude</div>
								<div className="stat-value">
									{currentAltitude.toFixed(1)}
									<span className="stat-unit">m</span>
								</div>
							</StatItem>
							<StatItem>
								<div className="stat-label">Ground Width</div>
								<div className="stat-value">
									{Math.sqrt(dspSettings.plotArea / 0.75).toFixed(1)}
									<span className="stat-unit">m</span>
								</div>
							</StatItem>
							<StatItem>
								<div className="stat-label">Plot Area</div>
								<div className="stat-value">
									{dspSettings.plotArea}
									<span className="stat-unit">m²</span>
								</div>
							</StatItem>
							<StatItem>
								<div className="stat-label">Lens</div>
								<div className="stat-value" style={{ fontSize: '0.85rem' }}>
									{DSP_LENSES[dspSettings.lens]?.name}
								</div>
							</StatItem>
						</StatsGrid>
					</FlightStatsCard>

					<AdvancedToggle onClick={onToggleDspSettings}>
						{showDspSettings ? (
							<LuChevronDown size={14} />
						) : (
							<LuChevronRight size={14} />
						)}{' '}
						Settings
					</AdvancedToggle>
				</SectionContent>
			</SidebarSection>

			{showDspSettings && (
				<>
					<DspCameraSettings
						dspSettings={dspSettings}
						onUpdateDsp={updateDsp}
						currentAltitude={currentAltitude}
					/>

					<SidebarSection>
						<SectionTitle>Sample Point Placement</SectionTitle>
						<SectionContent>
							<FormGroup>
								<label>Point Count:</label>
								<div
									style={{
										display: 'flex',
										gap: '0.5rem',
										alignItems: 'center',
									}}>
									<input
										type="range"
										min="1"
										max="1000"
										value={dspSettings.pointCount}
										onChange={e =>
											updateDsp('pointCount', parseInt(e.target.value))
										}
										style={{ flex: 1 }}
									/>
									<input
										type="number"
										min="1"
										max="5000"
										value={dspSettings.pointCount}
										onChange={e => {
											const raw = e.target.value;
											if (raw === '') {
												updateDsp('pointCount', '');
												return;
											}
											const val = parseInt(raw);
											if (!isNaN(val))
												updateDsp('pointCount', Math.max(1, val));
										}}
										onBlur={() => {
											if (
												dspSettings.pointCount === '' ||
												isNaN(dspSettings.pointCount)
											)
												updateDsp('pointCount', 10);
										}}
										style={{ width: '60px' }}
									/>
								</div>
							</FormGroup>

							<FormGroup>
								<label>Distribution:</label>
								<select
									value={dspSettings.distributionMethod}
									onChange={e => {
										const newMethod = e.target.value;
										updateDsp('distributionMethod', newMethod);
										// Clear waypoints when switching to manual mode
										if (newMethod === 'manual') {
											onClearWaypoints();
											onToggleAddingDsp(false);
										}
									}}>
									<option value="even">Even Distribution</option>
									<option value="random">Random</option>
									<option value="manual">Manual Placement</option>
								</select>
							</FormGroup>

							{dspSettings.distributionMethod === 'manual' && (
								<div style={{ marginBottom: '1rem' }}>
									<ActionButton
										variant={isAddingDsp ? 'danger' : 'primary'}
										onClick={() => onToggleAddingDsp()}
										style={{ marginBottom: '0.5rem' }}>
										{isAddingDsp ? (
											<>
												<LuX
													size={14}
													style={{
														verticalAlign: 'middle',
														marginRight: '4px',
													}}
												/>{' '}
												Stop Adding DSPs
											</>
										) : (
											<>
												<LuMapPin
													size={14}
													style={{
														verticalAlign: 'middle',
														marginRight: '4px',
													}}
												/>{' '}
												Add DSP Point
											</>
										)}
									</ActionButton>
									{waypoints.length > 2 && (
										<ActionButton
											variant="outline-info"
											onClick={onOptimizeRoute}
											style={{ marginBottom: '0.5rem' }}>
											<LuRefreshCw
												size={14}
												style={{
													verticalAlign: 'middle',
													marginRight: '4px',
												}}
											/>{' '}
											Optimize Route Order
										</ActionButton>
									)}
									{isAddingDsp && (
										<p
											style={{
												fontSize: '0.8rem',
												color: '#00ffff',
												marginTop: '0.5rem',
											}}>
											Click on the map to place DSP waypoints
										</p>
									)}
								</div>
							)}

							<FormGroup>
								<label>
									Grid Alignment:{' '}
									<span className="range-value">
										{dspSettings.alignmentAngle}°
									</span>
								</label>
								<input
									type="range"
									min="0"
									max="180"
									step="15"
									value={dspSettings.alignmentAngle}
									onChange={e =>
										updateDsp('alignmentAngle', parseInt(e.target.value))
									}
								/>
								<small style={{ color: '#888', fontSize: '0.75rem' }}>
									0° = North-South, 90° = East-West
								</small>
							</FormGroup>

							<FormGroup>
								<label>
									Flight Speed:{' '}
									<span className="range-value">{dspSettings.speed} m/s</span>
								</label>
								<input
									type="range"
									min="2"
									max="15"
									value={dspSettings.speed}
									onChange={e => updateDsp('speed', parseInt(e.target.value))}
								/>
							</FormGroup>

							<ToggleGroup>
								<label>Stop at Each DSP</label>
								<Toggle
									$active={dspSettings.stopAtDsp}
									onClick={() => updateDsp('stopAtDsp', !dspSettings.stopAtDsp)}
								/>
							</ToggleGroup>

							{dspSettings.stopAtDsp && (
								<FormGroup>
									<label>
										Stop Duration:{' '}
										<span className="range-value">
											{dspSettings.stopDuration}s
										</span>
									</label>
									<div
										style={{
											display: 'flex',
											gap: '0.5rem',
											alignItems: 'center',
										}}>
										<input
											type="range"
											min="1"
											max="30"
											value={dspSettings.stopDuration}
											onChange={e =>
												updateDsp('stopDuration', parseInt(e.target.value))
											}
											style={{ flex: 1 }}
										/>
										<input
											type="number"
											min="1"
											max="60"
											value={dspSettings.stopDuration}
											onChange={e => {
												const raw = e.target.value;
												if (raw === '') {
													updateDsp('stopDuration', '');
													return;
												}
												const val = parseInt(raw);
												if (!isNaN(val))
													updateDsp(
														'stopDuration',
														Math.min(60, Math.max(1, val))
													);
											}}
											onBlur={() => {
												if (
													dspSettings.stopDuration === '' ||
													isNaN(dspSettings.stopDuration)
												)
													updateDsp('stopDuration', 3);
											}}
											style={{ width: '50px' }}
										/>
									</div>
									<small style={{ color: '#888', fontSize: '0.75rem' }}>
										Hover time at each DSP point for image capture
									</small>
								</FormGroup>
							)}

							<FormGroup>
								<label>
									Gimbal Pitch:{' '}
									<span className="range-value">{dspSettings.gimbalPitch}°</span>
								</label>
								<input
									type="range"
									min="-90"
									max="0"
									step="5"
									value={dspSettings.gimbalPitch}
									onChange={e =>
										updateDsp('gimbalPitch', parseInt(e.target.value))
									}
								/>
								<small style={{ color: '#888', fontSize: '0.75rem' }}>
									-90° = straight down (nadir), 0° = horizontal
								</small>
							</FormGroup>

							<ToggleGroup>
								<label>Terrain Following</label>
								<Toggle
									$active={dspSettings.terrainFollow}
									onClick={() =>
										updateDsp('terrainFollow', !dspSettings.terrainFollow)
									}
								/>
							</ToggleGroup>

							{FEATURE_FLAGS.HEIGHT_MEASUREMENT_ENABLED && (
								<ToggleGroup>
									<label>Height Measurement</label>
									<Toggle
										$active={dspSettings.heightMeasurementEnabled}
										onClick={() =>
											updateDsp(
												'heightMeasurementEnabled',
												!dspSettings.heightMeasurementEnabled
											)
										}
									/>
								</ToggleGroup>
							)}

							{FEATURE_FLAGS.HEIGHT_MEASUREMENT_ENABLED &&
								dspSettings.heightMeasurementEnabled && (
									<div
										style={{
											marginTop: '0.75rem',
											padding: '0.75rem',
											backgroundColor: 'rgba(255, 165, 0, 0.1)',
											borderRadius: '6px',
											border: '1px solid rgba(255, 165, 0, 0.4)',
										}}>
										<p
											style={{
												fontSize: '0.85rem',
												fontWeight: '600',
												color: '#ffa500',
												marginBottom: '0.5rem',
											}}>
											<LuRuler
												size={14}
												style={{
													verticalAlign: 'middle',
													marginRight: '4px',
												}}
											/>{' '}
											Height Measurement Mode
										</p>
										<p
											style={{
												fontSize: '0.8rem',
												color: '#ccc',
												marginBottom: '0.5rem',
											}}>
											At each DSP waypoint, the drone will first collect
											height calibration data before capturing the DSP image.
										</p>
										<ol
											style={{
												fontSize: '0.8rem',
												paddingLeft: '1.25rem',
												margin: 0,
												lineHeight: 1.6,
											}}>
											<li>
												Pilot confirms open canopy area (ground visible)
											</li>
											<li>
												Drone switches to wide-angle lens, gimbal 90° down
											</li>
											<li>Laser Range Finder (LRF) activates</li>
											<li>Drone flies forward at 1 m/s for 7 seconds</li>
											<li>7 images captured (1 per second) with LRF data</li>
											<li>Then continues with normal DSP capture</li>
										</ol>
										<p
											style={{
												fontSize: '0.75rem',
												color: '#888',
												marginTop: '0.5rem',
												fontStyle: 'italic',
											}}>
											<LuTriangleAlert
												size={12}
												style={{
													verticalAlign: 'middle',
													marginRight: '4px',
												}}
											/>
											Requires open canopy where camera can see the ground
										</p>
									</div>
								)}

							<div
								style={{
									marginTop: '1rem',
									padding: '0.75rem',
									backgroundColor: 'rgba(0, 255, 255, 0.1)',
									borderRadius: '6px',
									border: '1px solid rgba(0, 255, 255, 0.3)',
								}}>
								<p
									style={{
										fontSize: '0.85rem',
										fontWeight: '600',
										color: '#00ffff',
										marginBottom: '0.5rem',
									}}>
									<LuSatellite
										size={14}
										style={{
											verticalAlign: 'middle',
											marginRight: '4px',
										}}
									/>{' '}
									DSP Flight Workflow
								</p>
								<ol
									style={{
										fontSize: '0.8rem',
										paddingLeft: '1.25rem',
										margin: 0,
										lineHeight: 1.6,
									}}>
									<li>Drone flies to DSP center point</li>
									<li>Gimbal tilts to {dspSettings.gimbalPitch}°</li>
									<li>Laser Range Finder (LRF) activates</li>
									<li>LRF distance confirms correct altitude</li>
									<li>Photo captured at calculated height</li>
								</ol>
							</div>

							<AdvancedToggle onClick={() => onToggleDspAdvanced()}>
								{showDspAdvanced ? (
									<LuChevronDown size={14} />
								) : (
									<LuChevronRight size={14} />
								)}
								Advanced Options
							</AdvancedToggle>

							{showDspAdvanced && (
								<div style={{ marginTop: '0.75rem' }}>
									<div
										style={{
											background: 'rgba(0, 255, 255, 0.1)',
											border: '1px solid rgba(0, 255, 255, 0.3)',
											borderRadius: '8px',
											padding: '0.75rem',
											marginBottom: '1rem',
										}}>
										<div
											style={{
												fontWeight: 500,
												marginBottom: '0.25rem',
												color: '#00ffff',
											}}>
											<LuMap
												size={14}
												style={{
													verticalAlign: 'middle',
													marginRight: '4px',
												}}
											/>{' '}
											Auto Coverage
										</div>
										<p
											style={{
												fontSize: '0.75rem',
												color: '#888',
												marginBottom: '0.5rem',
											}}>
											Automatically place DSP points to cover the entire area.
											Useful for surveying large areas without photogrammetry.
										</p>
										<FormGroup style={{ marginBottom: '0.5rem' }}>
											<label>Overlap / Gap (%):</label>
											<div
												style={{
													display: 'flex',
													gap: '0.5rem',
													alignItems: 'center',
												}}>
												<input
													type="range"
													min="-20"
													max="50"
													step="5"
													value={dspSettings.maxOverlap}
													onChange={e =>
														updateDsp(
															'maxOverlap',
															parseInt(e.target.value)
														)
													}
													style={{ flex: 1 }}
												/>
												<input
													type="number"
													min="-50"
													max="50"
													value={dspSettings.maxOverlap}
													onChange={e =>
														updateDsp(
															'maxOverlap',
															Math.min(
																50,
																Math.max(
																	-50,
																	parseInt(e.target.value) || 0
																)
															)
														)
													}
													style={{ width: '55px' }}
												/>
												<span>%</span>
											</div>
											<small
												style={{
													color: '#888',
													fontSize: '0.75rem',
												}}>
												{dspSettings.maxOverlap >= 0
													? `${dspSettings.maxOverlap}% overlap between images`
													: `${Math.abs(dspSettings.maxOverlap)}% gap/buffer between images`}
											</small>
										</FormGroup>
										<ActionButton
											variant="success"
											onClick={onGenerateAutoCoverage}
											style={{ width: '100%' }}>
											<LuMove
												size={14}
												style={{
													verticalAlign: 'middle',
													marginRight: '4px',
												}}
											/>{' '}
											Auto-Place for Full Coverage
										</ActionButton>
									</div>

									<ToggleGroup>
										<label>Distance Constraints</label>
										<Toggle
											$active={showDistanceConstraints}
											onClick={onToggleDistanceConstraints}
										/>
									</ToggleGroup>

									{showDistanceConstraints && (
										<>
											<FormGroup>
												<label>Minimum Distance (m):</label>
												<input
													type="number"
													min="10"
													max="500"
													value={dspSettings.minimumDistance}
													onChange={e => {
														const raw = e.target.value;
														if (raw === '') {
															updateDsp('minimumDistance', '');
															return;
														}
														const val = parseInt(raw);
														if (!isNaN(val))
															onDspSettingsChange({
																...dspSettings,
																minimumDistance: val,
																maximumDistance: Math.max(
																	dspSettings.maximumDistance,
																	val
																),
															});
													}}
													onBlur={() => {
														if (
															dspSettings.minimumDistance === '' ||
															isNaN(dspSettings.minimumDistance)
														)
															updateDsp('minimumDistance', 50);
													}}
												/>
											</FormGroup>

											<FormGroup>
												<label>Maximum Distance (m):</label>
												<input
													type="number"
													min="10"
													max="1000"
													value={dspSettings.maximumDistance}
													onChange={e => {
														const raw = e.target.value;
														if (raw === '') {
															updateDsp('maximumDistance', '');
															return;
														}
														const val = parseInt(raw);
														if (!isNaN(val))
															onDspSettingsChange({
																...dspSettings,
																maximumDistance: val,
																minimumDistance: Math.min(
																	dspSettings.minimumDistance,
																	val
																),
															});
													}}
													onBlur={() => {
														if (
															dspSettings.maximumDistance === '' ||
															isNaN(dspSettings.maximumDistance)
														)
															updateDsp('maximumDistance', 200);
													}}
												/>
												<small
													style={{
														color: '#888',
														fontSize: '0.75rem',
													}}>
													Ensures area coverage without large gaps
												</small>
											</FormGroup>
										</>
									)}
								</div>
							)}

							<DspMissionStats
								waypoints={waypoints}
								polygonPoints={polygonPoints}
								dspSettings={dspSettings}
							/>
						</SectionContent>
					</SidebarSection>

					<AiModelSection
						aiModelUuid={projectSettings.aiModelUuid}
						onAiModelChange={value => updateProjectField('aiModelUuid', value)}
						availableAiModels={availableAiModels}
						helpText={{
							selected:
								'Model runs automatically on the flight polygon after data sync',
							none: 'No automatic detection after processing',
						}}
					/>
				</>
			)}
		</>
	);
};

export default memo(WorkflowDspConfig);
